import { DOCUMENT } from '@angular/common';
import {
    Inject,
    Injectable,
    InjectionToken,
    NgZone,
    OnDestroy,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { SocketService } from './socket.service';

export interface DeviceClock {
    wallNow(): number;
    monotonicNow(): number;
}

export interface ServerClockSettings {
    samplesPerSync: number;
    sampleSpacingMs: number;
    sampleTimeoutMs: number;
    maxRoundTripMs: number;
    minSyncSpacingMs: number;
    resyncIntervalMs: number;
    failureBackoffMs: number;
    maxFailureBackoffMs: number;
    watchIntervalMs: number;
    clockJumpMs: number;
    sentAheadMs: number;
    easeBelowMs: number;
    easeDurationMs: number;
    storedOffsetMaxAgeMs: number;
    settleDeadlineMs: number;
    unsettledWindowMs: number;
}

export const DEFAULT_SERVER_CLOCK_SETTINGS: ServerClockSettings = {
    samplesPerSync: 5,
    sampleSpacingMs: 300,
    sampleTimeoutMs: 2000,
    maxRoundTripMs: 1000,
    minSyncSpacingMs: 10_000,
    resyncIntervalMs: 12 * 60_000,
    failureBackoffMs: 30_000,
    maxFailureBackoffMs: 10 * 60_000,
    watchIntervalMs: 2000,
    clockJumpMs: 1000,
    sentAheadMs: 1000,
    easeBelowMs: 1000,
    easeDurationMs: 3000,
    storedOffsetMaxAgeMs: 10 * 60_000,
    settleDeadlineMs: 5000,
    unsettledWindowMs: 5 * 60_000,
};

export const DEVICE_CLOCK: InjectionToken<DeviceClock> =
    new InjectionToken<DeviceClock>('DEVICE_CLOCK', {
        providedIn: 'root',
        factory: (): DeviceClock => ({
            wallNow: (): number => Date.now(),
            monotonicNow: (): number => performance.now(),
        }),
    });

export const SERVER_CLOCK_SETTINGS: InjectionToken<ServerClockSettings> =
    new InjectionToken<ServerClockSettings>('SERVER_CLOCK_SETTINGS', {
        providedIn: 'root',
        factory: (): ServerClockSettings => DEFAULT_SERVER_CLOCK_SETTINGS,
    });

export type OffsetStorage = Pick<Storage, 'getItem' | 'setItem'>;

export const SERVER_CLOCK_STORAGE: InjectionToken<OffsetStorage | null> =
    new InjectionToken<OffsetStorage | null>('SERVER_CLOCK_STORAGE', {
        providedIn: 'root',
        factory: (): OffsetStorage | null => {
            try {
                return sessionStorage;
            } catch {
                return null;
            }
        },
    });

export const STORED_OFFSET_KEY: string = 'vb.serverClockOffset';

export interface StoredOffset {
    serverMinusWallMs: number;
    savedAtWallMs: number;
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function storedOffsetOf(raw: string | null): StoredOffset | null {
    if (!raw) return null;
    let value: unknown;
    try {
        value = JSON.parse(raw);
    } catch {
        return null;
    }
    return typeof value === 'object' &&
        value !== null &&
        'serverMinusWallMs' in value &&
        isFiniteNumber(value.serverMinusWallMs) &&
        'savedAtWallMs' in value &&
        isFiniteNumber(value.savedAtWallMs)
        ? {
              serverMinusWallMs: value.serverMinusWallMs,
              savedAtWallMs: value.savedAtWallMs,
          }
        : null;
}

export type ServerTimeChannel = Pick<
    SocketService,
    'connected$' | 'sentAt$' | 'isConnected' | 'requestServerTime'
>;

export type VisibilitySource = EventTarget & Pick<Document, 'visibilityState'>;

interface TimeSample {
    serverMs: number;
    roundTripMs: number;
    receivedAtMonotonicMs: number;
}

interface ServerTimeAnchor {
    serverMs: number;
    monotonicMs: number;
    wallMinusMonotonicMs: number;
}

interface EasedCorrection {
    correctionMs: number;
    startedAtMonotonicMs: number;
}

@Injectable({ providedIn: 'root' })
export class ServerClockService implements OnDestroy {
    private anchor: ServerTimeAnchor | null = null;
    private easing: EasedCorrection | null = null;
    private readonly startingOffsetMs: number | null;
    private exchangeId: number = 0;
    private exchanging: boolean = false;
    private failedSyncs: number = 0;
    private nextSyncAllowedAtMonotonicMs: number = -Infinity;
    private announcedSleep: boolean = false;
    private settleDeadlinePassed: boolean = false;
    private readonly settleDeadline: ReturnType<typeof setTimeout>;
    private easedAnnouncement: ReturnType<typeof setTimeout> | null = null;
    private readonly watchTimer: ReturnType<typeof setInterval>;
    private readonly subscriptions: Subscription = new Subscription();
    private readonly correctedSubject: Subject<void> = new Subject<void>();
    readonly corrected$: Observable<void> =
        this.correctedSubject.asObservable();
    private readonly onVisibilityChange: () => void = (): void => {
        if (this.doc.visibilityState === 'visible') this.requestSync();
    };

    constructor(
        @Inject(SocketService) private readonly channel: ServerTimeChannel,
        private readonly zone: NgZone,
        @Inject(DOCUMENT) private readonly doc: VisibilitySource,
        @Inject(DEVICE_CLOCK) private readonly device: DeviceClock,
        @Inject(SERVER_CLOCK_SETTINGS)
        private readonly settings: ServerClockSettings,
        @Inject(SERVER_CLOCK_STORAGE)
        private readonly storage: OffsetStorage | null,
    ) {
        this.startingOffsetMs = this.freshStoredOffsetMs();
        this.subscriptions.add(
            channel.connected$.subscribe((): void => this.startSync()),
        );
        this.subscriptions.add(
            channel.sentAt$.subscribe((sentAt: number): void =>
                this.checkSentAt(sentAt),
            ),
        );
        this.doc.addEventListener('visibilitychange', this.onVisibilityChange);
        this.watchTimer = this.zone.runOutsideAngular(
            (): ReturnType<typeof setInterval> =>
                setInterval(() => this.watch(), settings.watchIntervalMs),
        );
        this.settleDeadline = this.zone.runOutsideAngular(
            (): ReturnType<typeof setTimeout> =>
                setTimeout(
                    () => this.passSettleDeadline(),
                    settings.settleDeadlineMs,
                ),
        );
        if (channel.isConnected) this.startSync();
    }

    now(): number {
        if (!this.anchor) {
            return this.device.wallNow() + (this.startingOffsetMs ?? 0);
        }
        if (this.hasDeviceSlept()) {
            return this.device.wallNow() + this.serverMinusWallMs(this.anchor);
        }
        const monotonicMs: number = this.device.monotonicNow();
        return (
            this.estimateAt(this.anchor, monotonicMs) -
            this.unappliedCorrectionAt(monotonicMs)
        );
    }

    get hasSettled(): boolean {
        return (
            !!this.anchor || this.failedSyncs > 0 || this.settleDeadlinePassed
        );
    }

    canJudgePassed(datetime: string | null | undefined): boolean {
        if (this.hasSettled || !datetime) return true;
        return (
            Math.abs(new Date(datetime).getTime() - this.now()) >
            this.settings.unsettledWindowMs
        );
    }

    ngOnDestroy(): void {
        this.exchangeId++;
        clearInterval(this.watchTimer);
        clearTimeout(this.settleDeadline);
        this.cancelEasedAnnouncement();
        this.subscriptions.unsubscribe();
        this.doc.removeEventListener(
            'visibilitychange',
            this.onVisibilityChange,
        );
    }

    private passSettleDeadline(): void {
        const settling: boolean = !this.hasSettled;
        this.settleDeadlinePassed = true;
        if (settling) this.announceCorrection();
    }

    private watch(): void {
        if (this.hasDeviceSlept() && !this.announcedSleep) {
            this.announcedSleep = true;
            this.announceCorrection();
        }
        if (this.isSyncDue() || this.hasDeviceClockJumped()) {
            this.requestSync();
        }
    }

    private checkSentAt(sentAt: number): void {
        if (sentAt - this.now() > this.settings.sentAheadMs) {
            this.requestSync();
        }
    }

    private isSyncDue(): boolean {
        return (
            !this.anchor ||
            this.device.monotonicNow() - this.anchor.monotonicMs >=
                this.settings.resyncIntervalMs
        );
    }

    private hasDeviceClockJumped(): boolean {
        return (
            !!this.anchor &&
            Math.abs(this.wallGainedOnMonotonicMs(this.anchor)) >
                this.settings.clockJumpMs
        );
    }

    private hasDeviceSlept(): boolean {
        return (
            !!this.anchor &&
            this.wallGainedOnMonotonicMs(this.anchor) >
                this.settings.clockJumpMs
        );
    }

    private wallGainedOnMonotonicMs(anchor: ServerTimeAnchor): number {
        return this.wallMinusMonotonic() - anchor.wallMinusMonotonicMs;
    }

    private requestSync(): void {
        if (
            this.exchanging ||
            !this.channel.isConnected ||
            this.device.monotonicNow() < this.nextSyncAllowedAtMonotonicMs
        ) {
            return;
        }
        this.startSync();
    }

    private startSync(): void {
        const id: number = ++this.exchangeId;
        this.exchanging = true;
        void this.exchange(id);
    }

    private async exchange(id: number): Promise<void> {
        let best: TimeSample | null = null;
        for (let i: number = 0; i < this.settings.samplesPerSync; i++) {
            if (i > 0) await this.pause(this.settings.sampleSpacingMs);
            if (id !== this.exchangeId) return;
            const sample: TimeSample | null = await this.takeSample();
            if (id !== this.exchangeId) return;
            if (!sample) break;
            if (this.improvesOn(best, sample)) {
                best = sample;
                this.adopt(sample);
            }
        }
        this.exchanging = false;
        if (best) {
            this.failedSyncs = 0;
            this.deferNextSync(this.settings.minSyncSpacingMs);
        } else {
            const settling: boolean = !this.hasSettled;
            this.failedSyncs++;
            this.deferNextSync(this.failureBackoffMs());
            if (settling) this.announceCorrection();
        }
    }

    private improvesOn(best: TimeSample | null, sample: TimeSample): boolean {
        return (
            sample.roundTripMs <= this.settings.maxRoundTripMs &&
            (!best || sample.roundTripMs < best.roundTripMs)
        );
    }

    private async takeSample(): Promise<TimeSample | null> {
        const sentAtMonotonicMs: number = this.device.monotonicNow();
        const serverMs: number | null = await this.channel.requestServerTime(
            this.settings.sampleTimeoutMs,
        );
        const receivedAtMonotonicMs: number = this.device.monotonicNow();
        return serverMs === null
            ? null
            : {
                  serverMs,
                  roundTripMs: receivedAtMonotonicMs - sentAtMonotonicMs,
                  receivedAtMonotonicMs,
              };
    }

    private adopt(sample: TimeSample): void {
        const next: ServerTimeAnchor = {
            serverMs: sample.serverMs + sample.roundTripMs / 2,
            monotonicMs: sample.receivedAtMonotonicMs,
            wallMinusMonotonicMs: this.wallMinusMonotonic(),
        };
        this.easing = this.anchor ? this.easeTowards(next) : null;
        this.anchor = next;
        this.announcedSleep = false;
        this.storeOffset(next);
        if (this.easing) {
            this.announceWhenEased();
        } else {
            this.announceCorrection();
        }
    }

    private easeTowards(next: ServerTimeAnchor): EasedCorrection | null {
        const monotonicMs: number = this.device.monotonicNow();
        const correctionMs: number =
            this.estimateAt(next, monotonicMs) - this.now();
        return Math.abs(correctionMs) < this.settings.easeBelowMs
            ? { correctionMs, startedAtMonotonicMs: monotonicMs }
            : null;
    }

    private estimateAt(anchor: ServerTimeAnchor, monotonicMs: number): number {
        return anchor.serverMs + (monotonicMs - anchor.monotonicMs);
    }

    private serverMinusWallMs(anchor: ServerTimeAnchor): number {
        return (
            anchor.serverMs - anchor.monotonicMs - anchor.wallMinusMonotonicMs
        );
    }

    private unappliedCorrectionAt(monotonicMs: number): number {
        if (!this.easing) return 0;
        const progress: number =
            (monotonicMs - this.easing.startedAtMonotonicMs) /
            this.settings.easeDurationMs;
        return this.easing.correctionMs * Math.max(0, 1 - progress);
    }

    private announceCorrection(): void {
        this.cancelEasedAnnouncement();
        this.zone.run(() => this.correctedSubject.next());
    }

    private announceWhenEased(): void {
        this.cancelEasedAnnouncement();
        this.easedAnnouncement = this.zone.runOutsideAngular(
            (): ReturnType<typeof setTimeout> =>
                setTimeout(
                    () => this.announceCorrection(),
                    this.settings.easeDurationMs,
                ),
        );
    }

    private cancelEasedAnnouncement(): void {
        if (this.easedAnnouncement !== null) {
            clearTimeout(this.easedAnnouncement);
            this.easedAnnouncement = null;
        }
    }

    private freshStoredOffsetMs(): number | null {
        const stored: StoredOffset | null = storedOffsetOf(this.readStorage());
        if (!stored) return null;
        const ageMs: number = this.device.wallNow() - stored.savedAtWallMs;
        return ageMs >= 0 && ageMs <= this.settings.storedOffsetMaxAgeMs
            ? stored.serverMinusWallMs
            : null;
    }

    private readStorage(): string | null {
        try {
            return this.storage?.getItem(STORED_OFFSET_KEY) ?? null;
        } catch {
            return null;
        }
    }

    private storeOffset(anchor: ServerTimeAnchor): void {
        const stored: StoredOffset = {
            serverMinusWallMs: this.serverMinusWallMs(anchor),
            savedAtWallMs: this.device.wallNow(),
        };
        try {
            this.storage?.setItem(STORED_OFFSET_KEY, JSON.stringify(stored));
        } catch {
            return;
        }
    }

    private wallMinusMonotonic(): number {
        return this.device.wallNow() - this.device.monotonicNow();
    }

    private failureBackoffMs(): number {
        return Math.min(
            this.settings.maxFailureBackoffMs,
            this.settings.failureBackoffMs * 2 ** (this.failedSyncs - 1),
        );
    }

    private deferNextSync(delayMs: number): void {
        this.nextSyncAllowedAtMonotonicMs =
            this.device.monotonicNow() + delayMs;
    }

    private pause(ms: number): Promise<void> {
        return new Promise<void>((resolve: () => void) =>
            this.zone.runOutsideAngular(() => setTimeout(resolve, ms)),
        );
    }
}
